import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Card } from '../../components/common/Card';
import { Heading, Text } from '../../components/common/Typography';
import { Button } from '../../components/common';
import ClubCard from '../../components/clubs/ClubCard';
import {
  fetchHomeMemberships,
  fetchHomeClubsSnapshot,
  fetchHomeClubsWinRates,
} from '../../services/homeDashboardService';

/**
 * ProfileClubsList - Landing page showing all clubs the player belongs to
 * Each club is a tappable card that navigates to the club detail page
 */
const ProfileClubsList = () => {
  const { currentUser } = useOutletContext();
  const navigate = useNavigate();

  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUserClubs = async () => {
      if (!currentUser?.id) return;

      try {
        setLoading(true);
        setInsightsLoading(true);
        const memberships = await fetchHomeMemberships(currentUser.id, { includeInactive: true });
        const snapshot = await fetchHomeClubsSnapshot(memberships, currentUser.id, { limit: null });
        setClubs(
          (snapshot.clubs || []).map((club) => ({
            id: club.id,
            name: club.name,
            location: club.location,
            banner_image_url: club.bannerImageUrl,
            role: club.role,
            membershipStatus: club.membershipStatus,
            clubWinRatePct: null,
          }))
        );
        setLoading(false);

        const withWinRates = await fetchHomeClubsWinRates(snapshot, currentUser.id);
        setClubs(
          (withWinRates.clubs || []).map((club) => ({
            id: club.id,
            name: club.name,
            location: club.location,
            banner_image_url: club.bannerImageUrl,
            role: club.role,
            membershipStatus: club.membershipStatus,
            clubWinRatePct: club.membershipStatus === 'inactive' ? null : club.clubWinRatePct,
          }))
        );
      } catch (err) {
        console.error('Error fetching user clubs:', err);
        setError('Failed to load clubs. Please try again.');
      } finally {
        setLoading(false);
        setInsightsLoading(false);
      }
    };

    fetchUserClubs();
  }, [currentUser?.id]);

  const handleClubClick = (clubId) => {
    navigate(`/clubs/${clubId}`, { state: { fromMyClubs: true } });
  };

  if (loading) {
    return (
      <div className="text-center py-12 text-slate-600">
        Loading clubs...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-600">
        {error}
      </div>
    );
  }

  if (clubs.length === 0) {
    return (
      <Card variant="default" size="large">
        <div className="text-center py-12">
          <Heading level={3} className="mb-2">
            No Clubs Found
          </Heading>
          <Text className="text-slate-600 mb-4">
            You're not a member of any clubs yet.
          </Text>
          <Button onClick={() => navigate('/create-club')}>
            Create a Club
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="mb-6">
        <Heading level={3} className="mb-2">
          My Clubs
        </Heading>
        <Text className="text-slate-600">
          Select a club to view your performance, partnerships, and matchup records.
        </Text>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {clubs.map((club) => (
          <ClubCard
            key={club.id}
            variant="member"
            layout="compact"
            insightsLoading={insightsLoading}
            club={club}
            clubWinRatePct={club.clubWinRatePct}
            onClick={() => handleClubClick(club.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default ProfileClubsList;
